<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import AuditLogsAPI from 'dashboard/api/auditLogs';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);

const { t } = useI18n();
const route = useRoute();
const isExporting = ref(false);
const filters = reactive({
  activity: '',
  start_date: '',
  end_date: '',
});

const compactQuery = query => {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== '')
  );
};

const queryValue = value => {
  return Array.isArray(value) ? value[0] : value || '';
};

const filtersFromQuery = query => {
  return {
    activity: queryValue(query.activity),
    start_date: queryValue(query.start_date),
    end_date: queryValue(query.end_date),
  };
};

const activeFilters = computed(() => filtersFromQuery(route.query));
const hasActiveFilters = computed(() =>
  Object.values(activeFilters.value).some(value => value)
);
const isInvalidDateRange = computed(() => {
  return Boolean(
    filters.start_date &&
      filters.end_date &&
      filters.start_date > filters.end_date
  );
});

const syncFiltersFromQuery = query => {
  const queryFilters = filtersFromQuery(query);
  filters.activity = queryFilters.activity;
  filters.start_date = queryFilters.start_date;
  filters.end_date = queryFilters.end_date;
};

const fetchAuditLogs = async (page, queryFilters = activeFilters.value) => {
  try {
    await store.dispatch('auditlogs/fetch', { page, ...queryFilters });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  if (!translationKey) {
    return (
      auditLogItem.comment ||
      `${auditLogItem.auditable_type} ${auditLogItem.action}`
    );
  }

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const onPageChange = page => {
  router.push({
    name: 'auditlogs_list',
    query: compactQuery({ page, ...activeFilters.value }),
  });
};

const applyFilters = () => {
  if (isInvalidDateRange.value) {
    useAlert(t('AUDIT_LOGS.FILTERS.INVALID_DATE_RANGE'));
    return;
  }

  router.push({
    name: 'auditlogs_list',
    query: compactQuery({ page: 1, ...filters }),
  });
};

const clearFilters = () => {
  filters.activity = '';
  filters.start_date = '';
  filters.end_date = '';
  router.push({ name: 'auditlogs_list', query: { page: 1 } });
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const fileNameFromHeaders = headers => {
  const disposition = headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  return (
    match?.[1] || `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`
  );
};

const exportAuditLogs = async () => {
  isExporting.value = true;
  try {
    const response = await AuditLogsAPI.download(activeFilters.value);
    downloadBlob(
      new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      fileNameFromHeaders(response.headers)
    );
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.FILTERS.EXPORT_ERROR');
    useAlert(errorMessage);
  } finally {
    isExporting.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
});

watch(
  () => route.query,
  query => {
    syncFiltersFromQuery(query);
    fetchAuditLogs(
      Number(queryValue(query.page) || 1),
      filtersFromQuery(query)
    );
  },
  { immediate: true }
);

const tableHeaders = computed(() => {
  return [
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
  ];
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('AUDIT_LOGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUDIT_LOGS.HEADER')"
        :description="$t('AUDIT_LOGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col">
        <form
          class="grid items-end gap-3 p-3 mb-4 border rounded-lg border-n-weak bg-n-alpha-1 md:grid-cols-[minmax(18rem,1fr)_minmax(10rem,12rem)_minmax(10rem,12rem)_auto]"
          @submit.prevent="applyFilters"
        >
          <label class="grid min-w-0 gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('AUDIT_LOGS.FILTERS.ACTIVITY') }}
            </span>
            <input
              v-model.trim="filters.activity"
              type="search"
              class="w-full h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="$t('AUDIT_LOGS.FILTERS.ACTIVITY_PLACEHOLDER')"
              @keydown.enter.prevent="applyFilters"
              @search="applyFilters"
            />
          </label>

          <label class="grid gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('AUDIT_LOGS.FILTERS.START_DATE') }}
            </span>
            <input
              v-model="filters.start_date"
              type="date"
              class="w-full h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>

          <label class="grid gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('AUDIT_LOGS.FILTERS.END_DATE') }}
            </span>
            <input
              v-model="filters.end_date"
              type="date"
              class="w-full h-9 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>

          <div
            class="flex flex-wrap items-center gap-2 md:self-start md:justify-end md:pt-5"
          >
            <Button
              type="submit"
              sm
              icon="i-lucide-filter"
              :label="$t('AUDIT_LOGS.FILTERS.APPLY')"
              :disabled="isInvalidDateRange"
            />
            <Button
              type="button"
              sm
              slate
              faded
              icon="i-lucide-x"
              :label="$t('AUDIT_LOGS.FILTERS.CLEAR')"
              :disabled="!hasActiveFilters"
              @click="clearFilters"
            />
            <Button
              type="button"
              sm
              slate
              outline
              icon="i-lucide-download"
              :label="$t('AUDIT_LOGS.FILTERS.EXPORT')"
              :is-loading="isExporting"
              @click="exportAuditLogs"
            />
          </div>
        </form>

        <BaseTable
          :headers="tableHeaders"
          :items="records"
          :loading="uiFlags.fetchingList"
          :no-data-message="$t('AUDIT_LOGS.LIST.404')"
          show-header-when-empty
        >
          <template #row="{ items }">
            <BaseTableRow
              v-for="auditLogItem in items"
              :key="auditLogItem.id"
              :item="auditLogItem"
            >
              <template #default>
                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-12 whitespace-nowrap"
                  >
                    {{ generateLogText(auditLogItem) }}
                  </span>
                </BaseTableCell>

                <BaseTableCell>
                  <span
                    class="text-body-main text-n-slate-11 whitespace-nowrap"
                  >
                    {{
                      messageTimestamp(
                        auditLogItem.created_at,
                        'MMM dd, yyyy hh:mm a'
                      )
                    }}
                  </span>
                </BaseTableCell>

                <BaseTableCell class="w-36">
                  <span class="text-body-main text-n-slate-11">
                    {{ auditLogItem.remote_address }}
                  </span>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
        <PaginationFooter
          :current-page="Number(meta.currentPage)"
          :total-items="meta.totalEntries"
          :items-per-page="meta.perPage"
          class="!px-0"
          @update:current-page="onPageChange"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
